import React, { useState } from 'react';
import {
    Form, Input, Button, Upload, message, Row, Col, Breadcrumb, Grid, Switch, Tag, Space, Tooltip
} from 'antd';
import { UploadOutlined, RightOutlined, PlusOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { acheteTerrainService } from '../../common/services';

const AjoutSitePage = () => {
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const screens = Grid.useBreakpoint();
    const [lotsPreviewCount, setLotsPreviewCount] = useState(0);
    const [lotsFile, setLotsFile] = useState(null); // fichier JSON optionnel

    // Normalise la valeur Upload -> fileList
    const normFile = (e) => (Array.isArray(e) ? e : e?.fileList);

    const handleSubmit = async (values) => {
        try {
            // Lecture éventuelle du fichier JSON de lots
            let lots = [];
            if (lotsFile) {
                const text = await lotsFile.text();
                const parsed = JSON.parse(text);
                if (!Array.isArray(parsed)) {
                    message.error('Le fichier JSON doit contenir un tableau de lots.');
                    return;
                }
                lots = parsed.map((l) => ({
                    intitule: String(l.intitule || '').trim(),
                    superficieM2: Number(l.superficieM2 || 0),
                    prixTotal: Number(l.prixTotal || 0),
                    remarque: l.remarque || undefined,
                    status: 'disponible',
                })).filter(x => x.intitule && x.superficieM2 > 0 && x.prixTotal > 0);
            }

            const payload = {
                nom: values.nom?.trim(),
                localisation: values.localisation?.trim(),
                estViabilise: !!values.estViabilise,
                caracteristiques: (values.caracteristiques || []).map((s) => String(s).trim()).filter(Boolean),
                lots, // optionnel
            };

            await acheteTerrainService.createSite(payload);
            message.success('Site créé avec succès');
            navigate('/dashboard/sites'); // adapte ton chemin si besoin
        } catch (error) {
            console.error(error);
            message.error("Erreur lors de la création du site");
        }
    };

    return (
        <div className="content-wrapper">
            <div className="container-xxl flex-grow-1 container-p-y">
                <div className="row">
                    <div className="col-md-12">

                        <Breadcrumb
                            separator={<RightOutlined style={{ fontSize: 16, color: '#999', fontWeight: 'bolder' }} />}
                            style={{ marginBottom: 16 }}
                        >
                            <Breadcrumb.Item>
                                <Link to="/dashboard" style={{ fontSize: 16, fontWeight: 'bolder' }}>🏠 ACCUEIL</Link>
                            </Breadcrumb.Item>
                            <Breadcrumb.Item>
                                <Link to="/dashboard/sites" style={{ fontSize: 16, fontWeight: 'bolder' }}>🧱 SITES</Link>
                            </Breadcrumb.Item>
                            <Breadcrumb.Item>
                                <span style={{ fontSize: 16, fontWeight: 'bolder' }}>➕ AJOUT</span>
                            </Breadcrumb.Item>
                        </Breadcrumb>

                        <div className="card mb-4">
                            <div className="card-body">
                                <Form
                                    layout="vertical"
                                    form={form}
                                    onFinish={handleSubmit}
                                    initialValues={{ estViabilise: false, caracteristiques: [] }}
                                >
                                    <Row gutter={[16, 16]}>
                                        <Col xs={24} md={12}>
                                            <Form.Item
                                                name="nom"
                                                label="Nom du site"
                                                rules={[{ required: true, message: 'Veuillez saisir le nom du site' }]}
                                            >
                                                <Input placeholder="Ex : Site de Bingerville" />
                                            </Form.Item>
                                        </Col>

                                        <Col xs={24} md={12}>
                                            <Form.Item
                                                name="localisation"
                                                label="Localisation"
                                                rules={[{ required: true, message: 'Veuillez saisir la localisation' }]}
                                            >
                                                <Input placeholder="Ex : Abidjan – Bingerville" />
                                            </Form.Item>
                                        </Col>

                                        <Col xs={24} md={12}>
                                            <Form.Item
                                                name="estViabilise"
                                                label={
                                                    <Space>
                                                        Viabilisé
                                                        <Tooltip title="Voirie, eau, électricité, etc.">
                                                            <InfoCircleOutlined />
                                                        </Tooltip>
                                                    </Space>
                                                }
                                                valuePropName="checked"
                                            >
                                                <Switch />
                                            </Form.Item>
                                        </Col>

                                        <Col xs={24} md={12}>
                                            <Form.Item
                                                name="caracteristiques"
                                                label={
                                                    <Space>
                                                        Caractéristiques (tags)
                                                        <Tooltip title="Ex : Clôturé, Électricité, Eau, Voirie">
                                                            <InfoCircleOutlined />
                                                        </Tooltip>
                                                    </Space>
                                                }
                                            >
                                                <TagsInput />
                                            </Form.Item>
                                        </Col>

                                        <Col xs={24}>
                                            <Form.Item
                                                name="lotsFile"
                                                label={
                                                    <Space>
                                                        Importer des lots (JSON) — optionnel
                                                        <Tooltip title='Format: [{"intitule":"Lot A1","superficieM2":300,"prixTotal":3000000,"remarque":"..."}]'>
                                                            <InfoCircleOutlined />
                                                        </Tooltip>
                                                    </Space>
                                                }
                                                valuePropName="fileList"
                                                getValueFromEvent={normFile}
                                            >
                                                <Upload
                                                    beforeUpload={() => false}
                                                    accept=".json,application/json"
                                                    maxCount={1}
                                                    onChange={(info) => {
                                                        const f = info?.fileList?.[0]?.originFileObj;
                                                        setLotsFile(f || null);
                                                        setLotsPreviewCount(!!f ? 0 : 0);
                                                    }}
                                                >
                                                    <Button icon={<UploadOutlined />}>Choisir un fichier JSON</Button>
                                                </Upload>
                                            </Form.Item>
                                            {lotsFile && (
                                                <div style={{ marginTop: 4 }}>
                                                    <Tag color="blue">Fichier sélectionné</Tag>
                                                </div>
                                            )}
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col xs={24}>
                                            <Form.Item style={{ marginBottom: 0, textAlign: screens.md ? 'right' : 'initial' }}>
                                                <Space wrap>
                                                    <Button onClick={() => navigate('/dashboard/sites')}>
                                                        Annuler
                                                    </Button>
                                                    <Button
                                                        type="primary"
                                                        htmlType="submit"
                                                        icon={<PlusOutlined />}
                                                        block={!screens.md}
                                                    >
                                                        Enregistrer le site
                                                    </Button>
                                                </Space>
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                </Form>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
};

export default AjoutSitePage;

/** ---- Petit composant pour gérer des tags libres dans un Form antd ---- */
const TagsInput = ({ value = [], onChange }) => {
    const [input, setInput] = useState('');
    const values = Array.isArray(value) ? value : [];

    const add = () => {
        const v = input.trim();
        if (!v) return;
        const next = Array.from(new Set([...(values || []), v]));
        onChange?.(next);
        setInput('');
    };

    const remove = (tag) => {
        const next = (values || []).filter((t) => t !== tag);
        onChange?.(next);
    };

    return (
        <div>
            <Space wrap style={{ marginBottom: 8 }}>
                {values.map((t) => (
                    <Tag key={t} closable onClose={() => remove(t)} color="processing">
                        {t}
                    </Tag>
                ))}
            </Space>
            <Space.Compact style={{ width: '100%' }}>
                <Input
                    placeholder="Ajouter une caractéristique…"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onPressEnter={add}
                />
                <Button type="primary" onClick={add}>Ajouter</Button>
            </Space.Compact>
        </div>
    );
};
